import { useEffect, useRef } from 'react';
import GUI, { Controller } from 'lil-gui';
import useSimulationStore from '../store/simulationStore';
import { shallow } from 'zustand/shallow';

// Hook to populate a lil-gui instance with view settings
export const useViewSettingsGui = (gui: GUI | null) => {
  // Select only the state and actions needed for this hook
  const {
    cameraFov,
    backgroundColor,
    ambientLightIntensity,
    viewportScaleFactor,
    activeVisShader, // Still need this for initial state
    setCameraFov,
    setBackgroundColor,
    setAmbientLightIntensity,
    setViewportScaleFactor,
    uiFeatures,
    // REMOVED: setActiveVisShader - will call directly via getState()
  } = useSimulationStore(
    // Use a selector for better performance and type safety
    (state) => ({
        cameraFov: state.cameraFov,
        backgroundColor: state.backgroundColor,
        ambientLightIntensity: state.ambientLightIntensity,
        viewportScaleFactor: state.viewportScaleFactor,
        activeVisShader: state.activeVisShader,
        setCameraFov: state.setCameraFov,
        setBackgroundColor: state.setBackgroundColor,
        setAmbientLightIntensity: state.setAmbientLightIntensity,
        setViewportScaleFactor: state.setViewportScaleFactor,
        uiFeatures: state.uiFeatures,
    }),
    shallow // Use shallow comparison if selecting an object
  );

  const fovControllerRef = useRef<Controller | null>(null);
  const bgColorControllerRef = useRef<Controller | null>(null);
  const ambientControllerRef = useRef<Controller | null>(null);
  const scaleFactorControllerRef = useRef<Controller | null>(null);
  const shaderControllerRef = useRef<Controller | null>(null); // Add ref for shader controller

  // Ref to store a plain object for lil-gui binding
  const guiStateRef = useRef({
    fov: cameraFov,
    bgColor: backgroundColor,
    ambient: ambientLightIntensity,
    scaleFactor: viewportScaleFactor,
    activeVisShader: activeVisShader,
  });

  // Update guiStateRef when selected state changes
  useEffect(() => {
      guiStateRef.current = {
        fov: cameraFov,
        bgColor: backgroundColor,
        ambient: ambientLightIntensity,
        scaleFactor: viewportScaleFactor,
        activeVisShader: activeVisShader,
      };
      // Update GUI display if needed (optional, sync effects below handle this)
      // fovControllerRef.current?.updateDisplay();
      // bgColorControllerRef.current?.updateDisplay();
      // ambientControllerRef.current?.updateDisplay();
      shaderControllerRef.current?.setValue(activeVisShader).updateDisplay(); // Update shader dropdown display
  }, [cameraFov, backgroundColor, ambientLightIntensity, viewportScaleFactor, activeVisShader]);

  // Effect to create/bind controllers
  useEffect(() => {
    if (!gui || uiFeatures.useNewControls) return;
    console.log('Binding GUI with View Settings');

    let folder = gui.folders.find(f => f._title === 'View Settings');
    if (!folder) {
      folder = gui.addFolder('View Settings');
    }

    // Define folder variable accessible in cleanup
    const currentFolder = folder;

    if (currentFolder) {
      // --- Helper to add/get controller and store ref ---
      const initController = (
        ref: React.MutableRefObject<Controller | null>,
        addMethod: 'add' | 'addColor',
        prop: keyof typeof guiStateRef.current,
        ...args: any[]
      ): Controller | null => {
        let ctrl = currentFolder!.controllers.find(c => c.property === prop) as Controller | undefined;
        if (!ctrl) {
            ctrl = currentFolder![addMethod](guiStateRef.current, prop, ...args) as Controller;
        }
        ref.current = ctrl || null;
        return ref.current;
      };

      // --- Initialize Controllers and set onChange handlers ---

      // FOV
      initController(fovControllerRef, 'add', 'fov', 30, 120, 1)
        ?.name('Camera FOV')
        .onChange((value: number) => {
          setCameraFov(value); // Update Zustand state
        });

      // Background Color
      initController(bgColorControllerRef, 'addColor', 'bgColor')
        ?.name('Background Color')
        .onChange((value: string) => {
          setBackgroundColor(value); // Update Zustand state
        });

      // Ambient Light
      initController(ambientControllerRef, 'add', 'ambient', 0, 5, 0.05)
        ?.name('Ambient Light')
        .onChange((value: number) => {
          setAmbientLightIntensity(value); // Update Zustand state
        });
      
      // Viewport Scale Factor
      initController(scaleFactorControllerRef, 'add', 'scaleFactor', 1, 10, 0.1)
        ?.name('Viewport Scale')
        .onFinishChange((value: number) => {
          setViewportScaleFactor(value);
          // Re-fit reactor to viewport with new scale factor
          const state = useSimulationStore.getState();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const cameraDistance = 50;
          state.fitReactorToViewport(viewportWidth, viewportHeight, cameraDistance, state.cameraFov, state.controls.is2DMode, value);
        });

      // Add Shader Selection Dropdown
      const shaderOptions = ['PBR_InstanceColor', 'SwirlEffect', 'TestBlue'];
      initController(shaderControllerRef, 'add', 'activeVisShader', shaderOptions)
        ?.name('Atom Shader')
        .onChange((value: string) => {
          // Use type assertion to call action via getState()
          const action = (useSimulationStore.getState() as any).setActiveVisShader;
          if (action) {
              action(value);
          } else {
              console.error("setActiveVisShader action missing from store state!");
          }
        });

      // Initial value is set by guiStateRef update effect
    }

    return () => {
        // Cleanup uses currentFolder which is stable within this effect's closure
        if (currentFolder) {
            console.log('[useViewSettingsGui] Destroying View Settings folder and controllers.');
            try {
                // Use .destroy() on the controllers themselves
                fovControllerRef.current?.destroy();
                bgColorControllerRef.current?.destroy();
                ambientControllerRef.current?.destroy();
                shaderControllerRef.current?.destroy();
                
                // Destroy the folder itself (which also removes controllers)
                // Check if it hasn't already been destroyed (e.g., by parent GUI destroy)
                if (!currentFolder._closed) {
                    currentFolder.destroy();
                }
            } catch(e) {
                console.error("Error during GUI cleanup:", e);
            }
            // Set refs to null after destroying
            fovControllerRef.current = null;
            bgColorControllerRef.current = null;
            ambientControllerRef.current = null;
            shaderControllerRef.current = null;
        } else {
             console.log('[useViewSettingsGui] No View Settings folder found on cleanup.');
        }
    };
  }, [gui]); // Dependency on gui only

  // REMOVE effects that manually sync Zustand -> GUI state 
  // (Handled by direct binding to guiStateRef and updating ref in useEffect)

}; 